import { scopedThreadKey } from "@cmmd-forge/client-runtime";
import type { ScopedThreadRef } from "@cmmd-forge/contracts";
import type { TimestampFormat } from "@cmmd-forge/contracts/settings";

import { useRightPanelStore } from "../../rightPanelStore";
import type { RightPanelKind } from "../../rightPanelSurface";
import type { DiffPanelMode } from "../DiffPanelShell";
import { RightSidebarShell, type RightSidebarShellProps } from "../RightSidebarShell";
import { RIGHT_PANEL_PANES } from "../rightPanelPaneRegistry";
import { useRightPanelPreviewAudio } from "../rightPanelPreviewAudio";
import { RightPanelMaximizeControl } from "./RightPanelMaximizeControl";

export interface ChatRightSidebarProps {
  readonly closeRightPanel: () => void;
  readonly closeRightPanelSurfaceTab: (surfaceId: string) => void;
  readonly activateRightPanelSurface: (surfaceId: string) => void;
  readonly resourceLabels: RightSidebarShellProps["resourceLabels"];
  readonly surfaces: RightSidebarShellProps["surfaces"];
  readonly activeSurfaceId: string | null;
  /** The active surface's kind; null while the panel shows its launcher. */
  readonly rightPanel: RightPanelKind | null;
  readonly mode: DiffPanelMode;
  readonly shouldRenderDiffContent: boolean;
  readonly previewTabId: string | undefined;
  readonly threadRef: ScopedThreadRef;
  readonly timestampFormat: TimestampFormat;
  readonly rightPanelMaximized: boolean;
  readonly onToggleRightPanelMaximized: () => void;
  readonly selectRightPanel: (kind: RightPanelKind) => void;
}

/**
 * The thread route's right panel.
 *
 * The pane is looked up in `RIGHT_PANEL_PANES` by the active surface's kind,
 * so registering a surface anywhere else is unnecessary and registering it
 * nowhere is impossible: the map is total over the kind union.
 */
export function ChatThreadRightSidebar(props: ChatRightSidebarProps) {
  const previewAudio = useRightPanelPreviewAudio(props.threadRef);
  const activeSurface =
    props.surfaces.find((surface) => surface.id === props.activeSurfaceId) ?? null;
  const resolved =
    props.rightPanel === null || activeSurface === null
      ? null
      : { pane: RIGHT_PANEL_PANES[props.rightPanel], surface: activeSurface };

  return (
    <RightSidebarShell
      surfaces={props.surfaces}
      activeSurfaceId={props.activeSurfaceId}
      isServerThread
      {...(props.resourceLabels !== undefined ? { resourceLabels: props.resourceLabels } : {})}
      onSelectSurface={props.activateRightPanelSurface}
      onCloseSurface={props.closeRightPanelSurfaceTab}
      onAddSurface={props.selectRightPanel}
      onRenameSurface={(surfaceId, label) => {
        useRightPanelStore
          .getState()
          .renameSurface(scopedThreadKey(props.threadRef), surfaceId, label);
      }}
      {...(previewAudio !== null ? { previewAudio } : {})}
      layoutControls={
        <RightPanelMaximizeControl
          maximized={props.rightPanelMaximized}
          onToggle={props.onToggleRightPanelMaximized}
        />
      }
      onClose={props.closeRightPanel}
    >
      {resolved === null ? null : (
        <resolved.pane
          threadRef={props.threadRef}
          surface={resolved.surface}
          mode={props.mode}
          shouldRenderDiffContent={props.shouldRenderDiffContent}
          previewTabId={props.previewTabId}
          timestampFormat={props.timestampFormat}
          closeRightPanel={props.closeRightPanel}
          selectRightPanel={props.selectRightPanel}
          closeSurface={props.closeRightPanelSurfaceTab}
        />
      )}
    </RightSidebarShell>
  );
}
